import React from 'react';
import styled, { css } from 'styled-components';
import { HappyCoLogoSVG, tokens, Button, Icon, Text, Avatar, ButtonIcon, MenuButton, MenuButtonIcon, Box, Menu, Inline, useQueryChildComponents, HappyWebSystemError } from '@happy-design/react';
import { CaretSVG, ActiveNavTriangleClipPath } from './Graphics.js';

const NavLogo = styled(HappyCoLogoSVG).attrs({
    viewBox: "0 0 28 20"
})`
  color: ${tokens.color.palette.white[100]};
  height: 1.5rem;
  width: 1.5rem;
  min-width: 1.5rem;
  margin-right: 0.25rem;

  &:hover {
    opacity: 0.8;
  }
`;
const StyledButton = styled(Button)`
  color: ${tokens.color.palette.white[100]};
  background-color: ${(props)=>props.theme.COLORS.context};

  height: 2rem;
  width: 100%;
  min-width: ${(props)=>props.minWidth ?? 10}rem;
  max-width: 12rem;
  padding: 0 0.75rem 0 0.5rem;

  justify-content: flex-start;
  text-align: left;

  border-radius: 0.5rem;

  &:hover,
  &:focus {
    background-color: ${(props)=>props.theme.COLORS.contextDark10};
  }

  ${(props)=>props.$active && css`
      color: ${(props)=>props.theme.COLORS.context};
      background-color: ${tokens.color.palette.white[100]};

      &:hover,
      &:focus {
        background-color: ${(props)=>props.theme.COLORS.context10};
      }

      &:before {
        content: '';
        position: absolute;
        background: ${tokens.color.palette.white[100]};

        width: 1rem;
        height: 0.5rem;

        clip-path: url(#activeNavClipPath);

        top: calc(100% + 0.45rem);
        left: calc(50% - 0.5rem);
      }
    `}

  & [${Icon.dataId}] {
    width: 1.25rem;
    min-width: 1.5rem;
    height: 1.375rem;
    font-size: 1.25rem;
  }

  & [${Icon.dataId}] > * {
    margin-left: 0;
  }

  & [${Text.dataId}] {
    line-height: 1.25rem;
  }
`;
const Caret = styled(CaretSVG)`
  color: inherit;
  width: 0.75rem;
  height: 0.75rem;
  margin-left: 0.375rem;
`;
const NavBarContextButton = ({ active , ...props })=>{
    return React.createElement(StyledButton, {
        "data-hd-navbar-context-button": true,
        "$active": active,
        ...props
    }, React.createElement(Text, {
        truncate: true,
        size: "medium"
    }, props.children), props.onClick !== undefined && React.createElement(Caret, null));
};
NavBarContextButton.dataId = "data-hd-navbar-context-button";
NavBarContextButton.displayName = "NavBarContextButton";
const StyledAvatar = styled(Avatar)`
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  min-height: 2rem;
  line-height: 2.125rem;
`;
const StyledButtonIcon = styled(ButtonIcon)`
  background-color: transparent;
  color: ${tokens.color.palette.white[100]};
  font-size: 1.15rem;
  padding: 0;

  height: 2rem;
  min-width: 2rem;
  &:hover,
  &:active,
  &:focus {
    background-color: transparent;
  }
  &:hover > * {
    opacity: 0.8;
  }
  & > [${Icon.dataId}] {
    position: fixed;
  }

  ${(props)=>props.$active && css`
      &:before {
        content: '';
        position: absolute;
        background: ${tokens.color.palette.white[100]};

        width: 1rem;
        height: 0.5rem;

        clip-path: url(#activeNavClipPath);

        top: calc(100% + 0.45rem);
        left: calc(50% - 0.5rem);
      }
    `}
`;
const StyledMenuButton = styled(MenuButton)`
  background-color: transparent;
  color: ${tokens.color.palette.white[100]};
  font-size: 1.15rem;
  padding: 0;

  height: 2rem;
  min-width: 2rem;
  &:hover,
  &:active,
  &:focus {
    background-color: transparent;
  }
  &:hover > * {
    opacity: 0.8;
  }
  & > [${Icon.dataId}] {
    position: fixed;
  }

  ${(props)=>props.$active && css`
      &:before {
        content: '';
        position: absolute;
        background: ${tokens.color.palette.white[100]};

        width: 1rem;
        height: 0.5rem;

        clip-path: url(#activeNavClipPath);

        top: calc(100% + 0.45rem);
        left: calc(50% - 0.5rem);
      }
    `}
`;
const StyledMenuButtonIcon = styled(MenuButtonIcon)`
  background-color: transparent;
  color: ${tokens.color.palette.white[100]};
  font-size: 1.15rem;
  height: 2rem;
  min-width: 2rem;
  &:hover,
  &:active,
  &:focus {
    background-color: transparent;
  }
  &:hover > * {
    opacity: 0.8;
  }
  & > [${ButtonIcon.dataId}] {
    position: fixed;
  }
  ${(props)=>props.$active && css`
      &:before {
        content: '';
        position: absolute;
        background: ${tokens.color.palette.white[100]};

        width: 1rem;
        height: 0.5rem;

        clip-path: url(#activeNavClipPath);

        top: calc(100% + 0.45rem);
        left: calc(50% - 0.5rem);
      }
    `}
`;
const NavBarUtilityButton = ({ active , children , ...props })=>{
    if (props.utility === "settings") {
        return React.createElement(StyledButtonIcon, {
            "data-hd-navbar-utility-button": true,
            testId: "Nav-Settings",
            icon: "settings",
            "$active": active,
            ...props
        }, children);
    }
    if (props.utility === "university" || props.utility === "help") {
        return React.createElement(Menu, {
            testId: "Nav-" + (props.utility === "university" ? "Uni" : "Help")
        }, React.createElement(StyledMenuButtonIcon, {
            "data-hd-navbar-utility-button": true,
            testId: "Nav-Button-" + (props.utility === "university" ? "Uni" : "Help"),
            icon: props.utility === "university" ? "happyco-university" : "help",
            "$active": active,
            ...props
        }), children);
    }
    if (props.utility === "profile") {
        return React.createElement(Menu, {
            testId: "Nav-Profile"
        }, React.createElement(StyledMenuButton, {
            "data-hd-navbar-utility-button": true,
            testId: "Nav-Button-Profile",
            "$active": active,
            ...props
        }, React.createElement(StyledAvatar, {
            colorKey: props.userId,
            initials: props.initials
        })), children);
    }
};
NavBarUtilityButton.dataId = "data-hd-navbar-utility-button";
NavBarUtilityButton.displayName = "NavBarUtilityButton";
const NavBarLeft = (props)=>{
    return React.createElement(Box, {
        "data-hd-navbar-left": true,
        padding: [
            1,
            1,
            1,
            1.5
        ],
        height: "fill",
        width: "content",
        flexGrow: 1
    }, React.createElement(Inline, {
        gap: 0.35,
        alignY: "center",
        wrap: false
    }, props.children));
};
NavBarLeft.dataId = "data-hd-navbar-left";
NavBarLeft.displayName = "NavBarLeft";
const RightBox = styled(Box)`
  & + & {
    border-left: 1px solid rgba(255, 255, 255, 0.15);
  }
`;
const NavBarRight = (props)=>{
    return React.createElement(RightBox, {
        "data-hd-navbar-right": true,
        padding: [
            0,
            1.5
        ],
        height: "fill",
        width: "content"
    }, React.createElement(Inline, {
        gap: 1,
        alignY: "center",
        wrap: false
    }, props.children));
};
NavBarRight.dataId = "data-hd-navbar-right";
NavBarRight.displayName = "NavBarRight";
const StyledNavbarBox = styled(Box)`
  background-color: ${(props)=>props.theme.COLORS.contextDark60};
  height: 3.5rem;
  border-top: none;
  border-left: none;
  border-right: none;
  min-width: 40rem;
  position: relative;
`;
const NavBar = (props)=>{
    const [navLeft, otherChildren] = useQueryChildComponents(props.children, [
        NavBarLeft
    ]);
    if (navLeft.length > 1) {
        throw new HappyWebSystemError(NavBarLeft, "Max of 1 left Nav element can be displayed in a NavBar");
    }
    const [navRight, _] = useQueryChildComponents(otherChildren, [
        NavBarRight
    ]);
    return React.createElement(StyledNavbarBox, {
        "data-hd-navbar": true,
        "aria-roledescription": "menubar"
    }, React.createElement(ActiveNavTriangleClipPath, {
        "aria-hidden": true
    }), React.createElement(Inline, {
        width: "fill",
        wrap: false
    }, navLeft, navRight));
};
NavBar.dataId = "data-hd-navbar";
NavBar.displayName = "NavBar";

export { NavBar, NavBarContextButton, NavBarLeft, NavBarRight, NavBarUtilityButton, NavLogo };
